import { useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useToast } from 'hooks/use-toast';
import {
  chatApprovalGrantsQueryKey,
  useGetChatApprovalGrants,
  useRevokeChatApprovalGrant,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IChatApprovalGrant } from 'features/chat/agent-chat-module/services/gpt-chat.service';

interface ChatPermissionsDialogProps {
  conversationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const grantKey = (grant: IChatApprovalGrant) => `${grant.toolId}::${grant.actionName}`;

const formatDate = (value: string) => {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? value : new Date(ms).toLocaleDateString();
};

/**
 * Lists the "Allow for this chat" grants handed out from HIL approval cards in the
 * open conversation, and lets the user revoke them one at a time. Fetches only
 * while open.
 */
export const ChatPermissionsDialog = ({
  conversationId,
  open,
  onOpenChange,
}: ChatPermissionsDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = chatApprovalGrantsQueryKey(conversationId);
  const { data: grants, isLoading } = useGetChatApprovalGrants(conversationId, { enabled: open });
  const { mutateAsync: revokeGrant } = useRevokeChatApprovalGrant();

  const handleRevoke = async (grant: IChatApprovalGrant) => {
    const previous = queryClient.getQueryData<IChatApprovalGrant[]>(queryKey);
    queryClient.setQueryData<IChatApprovalGrant[]>(queryKey, (current) =>
      (current ?? []).filter((item) => grantKey(item) !== grantKey(grant))
    );
    try {
      await revokeGrant({
        conversationId,
        toolId: grant.toolId,
        actionName: grant.actionName,
      });
      queryClient.invalidateQueries({ queryKey });
      toast({
        variant: 'success',
        title: t('PERMISSION_REVOKED', 'Permission revoked'),
        description: t('PERMISSION_REVOKED_DESCRIPTION', 'This tool will ask for approval again.'),
      });
    } catch {
      queryClient.setQueryData(queryKey, previous);
      toast({
        variant: 'destructive',
        title: t('PERMISSION_REVOKE_FAILED', 'Could not revoke permission'),
        description: t('PLEASE_TRY_AGAIN', 'Please try again.'),
      });
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (!grants?.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border bg-card py-10">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <p className="max-w-xs text-center text-xs sm:text-sm text-muted-foreground">
            {t(
              'NO_CHAT_PERMISSIONS',
              'No permissions granted in this chat. Approvals you allow for this chat appear here.'
            )}
          </p>
        </div>
      );
    }

    return (
      <ul className="max-h-[45vh] overflow-auto rounded-lg border bg-card">
        {grants.map((grant) => (
          <li
            key={grantKey(grant)}
            className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0 sm:px-4 sm:py-3"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-xs sm:text-sm font-medium text-foreground">
                {grant.toolName}
              </span>
              <span className="truncate text-[11px] sm:text-xs text-muted-foreground">
                {grant.actionName === '*' ? t('ALL_ACTIONS', 'All actions') : grant.actionName}
              </span>
              <span className="text-[11px] sm:text-xs text-muted-foreground">
                {t('GRANTED_ON', 'Granted on')} {formatDate(grant.grantedDate)}
              </span>
            </div>
            <Button size="sm" variant="outline" onClick={() => handleRevoke(grant)}>
              {t('REVOKE', 'Revoke')}
            </Button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('PERMISSIONS_IN_THIS_CHAT', 'Permissions in this chat')}</DialogTitle>
          <DialogDescription>
            {t(
              'PERMISSIONS_IN_THIS_CHAT_DESCRIPTION',
              'Tools allowed to run without asking again for the rest of this conversation.'
            )}
          </DialogDescription>
        </DialogHeader>
        {renderBody()}
      </DialogContent>
    </Dialog>
  );
};

export default ChatPermissionsDialog;
